import { useToast } from "@nabble/ui";
import { useCallback, useEffect, useState } from "react";
import { fetchBrandImages, removeBrandImage, uploadBrandImage, type BrandImageInfo } from "../../../api/adminBranding";
import { errorMessage } from "../../../errors";
import { brandFileProblem } from "../../../model/brandSlots";
import { readAsBase64 } from "./readAsBase64";

/** The stored brand images and the actions on them, with their toasts. */
export function useBrandImages() {
  const { show } = useToast();
  const [images, setImages] = useState<BrandImageInfo[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoadError(null);
    fetchBrandImages()
      .then(setImages)
      .catch((err: unknown) => setLoadError(errorMessage(err, "We couldn't load the brand images.")));
  }, []);

  useEffect(reload, [reload]);

  const run = useCallback(
    async (slot: string, action: () => Promise<void>, done: string): Promise<void> => {
      setBusy(slot);
      try {
        await action();
        show({ title: done, tone: "success" });
        reload();
      } catch (err) {
        show({ title: "We couldn't change the image", text: errorMessage(err, "Please try again."), tone: "danger" });
      } finally {
        setBusy(null);
      }
    },
    [reload, show],
  );

  const upload = useCallback(
    (slot: string, file: File): void => {
      const problem = brandFileProblem(file);
      if (problem) {
        show({ title: "We couldn't use this file", text: problem, tone: "danger" });
        return;
      }
      void run(slot, async () => uploadBrandImage(slot, await readAsBase64(file)), "Image saved");
    },
    [run, show],
  );

  const remove = useCallback((slot: string) => void run(slot, () => removeBrandImage(slot), "Image removed"), [run]);

  return { images, loadError, busy, reload, upload, remove };
}
